/**
 * Reminders to a work order's external delegates (molnfast-react#1613): the
 * timing, its form draft, the sent log and the Swedish labels. Pure; schema in
 * workspace docs/schema/work-and-reports.md and platform.md. The job and its
 * rules live in mf-functions (`workOrders/reminderSchedule.ts`, same ranges).
 */
import type { WorkOrderReminderKind, WorkOrderReminderLogEntry } from './workOrders';

export const WORK_ORDER_REMINDERS_PATH = 'global/workOrderReminders';

export const DAYS_BEFORE_MAX = 30;
export const REPEAT_EVERY_DAYS_MAX = 30;

/** The two numbers of a schedule; absent = that reminder is off. */
export interface ReminderTiming {
    daysBefore?: number;
    repeatEveryDays?: number;
}

/** What an instance without `global/workOrderReminders` uses. */
// Nothing before the due date: it is often the planned day (molnfast-react#1701).
export const DEFAULT_REMINDER_TIMING: ReminderTiming = { repeatEveryDays: 7 };

const isIntIn = (value: unknown, min: number, max: number): value is number =>
    typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max;

/** The valid numbers of a stored schedule (invalid ones are dropped). */
export const parseReminderTiming = (raw: unknown): ReminderTiming => {
    if (!raw || typeof raw !== 'object') return {};
    const r = raw as Record<string, unknown>;
    return {
        ...(isIntIn(r.daysBefore, 0, DAYS_BEFORE_MAX) ? { daysBefore: r.daysBefore } : {}),
        ...(isIntIn(r.repeatEveryDays, 1, REPEAT_EVERY_DAYS_MAX) ? { repeatEveryDays: r.repeatEveryDays } : {}),
    };
};

/** The instance default: absent node → nothing before, overdue every 7 days. */
export const parseReminderDefaults = (raw: unknown): ReminderTiming =>
    raw && typeof raw === 'object' ? parseReminderTiming(raw) : { ...DEFAULT_REMINDER_TIMING };

/** True for a stored schedule an admin turned off (`off: true`). */
export const isScheduleOff = (raw: unknown): boolean =>
    !!raw && typeof raw === 'object' && (raw as { off?: unknown }).off === true;

/** The timing a stored schedule sends, or null when absent or turned off. */
export const scheduleTiming = (raw: unknown): ReminderTiming | null =>
    raw && typeof raw === 'object' && !isScheduleOff(raw) ? parseReminderTiming(raw) : null;

// ============================================================================
// Form draft (composer, detail row, Aviseringar)
// ============================================================================

/** What the reminder fields hold while edited; an empty number = off. */
export interface ReminderDraft {
    enabled: boolean;
    daysBefore: string;
    repeatEveryDays: string;
}

export const draftFromTiming = (timing: ReminderTiming | null, enabled = timing !== null): ReminderDraft => ({
    enabled,
    daysBefore: timing?.daysBefore !== undefined ? String(timing.daysBefore) : '',
    repeatEveryDays: timing?.repeatEveryDays !== undefined ? String(timing.repeatEveryDays) : '',
});

export type DraftResult = { timing: ReminderTiming | null; error?: undefined } | { timing?: undefined; error: string };

/** The timing a draft stands for (null when turned off), or the Swedish error. */
export const timingFromDraft = (draft: ReminderDraft): DraftResult => {
    if (!draft.enabled) return { timing: null };
    const parse = (text: string, min: number, max: number): number | undefined | false => {
        const trimmed = text.trim();
        if (trimmed === '') return undefined;
        const value = Number(trimmed);
        return isIntIn(value, min, max) ? value : false;
    };
    const daysBefore = parse(draft.daysBefore, 0, DAYS_BEFORE_MAX);
    if (daysBefore === false) return { error: `Dagar före förfallodatum ska vara 0–${DAYS_BEFORE_MAX}` };
    const repeatEveryDays = parse(draft.repeatEveryDays, 1, REPEAT_EVERY_DAYS_MAX);
    if (repeatEveryDays === false) return { error: `Upprepning ska vara 1–${REPEAT_EVERY_DAYS_MAX} dagar` };
    return {
        timing: {
            ...(daysBefore !== undefined ? { daysBefore } : {}),
            ...(repeatEveryDays !== undefined ? { repeatEveryDays } : {}),
        },
    };
};

// ============================================================================
// Display
// ============================================================================

/** "3 dagar före förfallodatum · dagen efter, sedan var 7:e dag" */
export const describeReminderTiming = (timing: ReminderTiming | null): string => {
    if (!timing) return 'Inga påminnelser';
    const before = timing.daysBefore === undefined
        ? null
        : timing.daysBefore === 0
            ? 'på förfallodagen'
            : `${timing.daysBefore} ${timing.daysBefore === 1 ? 'dag' : 'dagar'} före förfallodatum`;
    const overdue = timing.repeatEveryDays === undefined
        ? 'vid försening dagen efter'
        : timing.repeatEveryDays === 1
            ? 'vid försening varje dag'
            : `vid försening dagen efter, sedan var ${timing.repeatEveryDays}:e dag`;
    return [before, overdue].filter(Boolean).join(' · ');
};

export const REMINDER_KIND_LABELS: Record<WorkOrderReminderKind, string> = {
    before: 'Påminnelse',
    overdue: 'Försenat',
    adminNotice: 'Avisering till delegerande',
};

/** A sent-log row with its key. */
export interface ReminderLogRow extends WorkOrderReminderLogEntry {
    key: string;
    recipients: string[];
    skipped: string[];
}

const KINDS: WorkOrderReminderKind[] = ['before', 'overdue', 'adminNotice'];

const uidList = (raw: unknown): string[] => {
    const values = Array.isArray(raw) ? raw : raw && typeof raw === 'object' ? Object.values(raw) : [];
    return values.filter((v): v is string => typeof v === 'string' && v.length > 0);
};

/** The sent log, newest first; malformed entries are skipped. */
export const parseReminderLog = (raw: unknown): ReminderLogRow[] => {
    if (!raw || typeof raw !== 'object') return [];
    const rows: ReminderLogRow[] = [];
    for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
        const e = value as Record<string, unknown> | null;
        if (!e || !KINDS.includes(e.kind as WorkOrderReminderKind) || typeof e.date !== 'string' || typeof e.dueDate !== 'string') {
            console.warn('Skipping invalid reminder log entry', key);
            continue;
        }
        rows.push({
            key,
            kind: e.kind as WorkOrderReminderKind,
            date: e.date,
            dueDate: e.dueDate,
            recipients: uidList(e.recipients),
            skipped: uidList(e.skipped),
        });
    }
    return rows.sort((a, b) => b.date.localeCompare(a.date) || a.key.localeCompare(b.key));
};
